import {
  BackBar,
  CHANNEL_LABELS,
  COMMITTEE_ROLE_LABELS,
  describeError,
  fullDate,
  IdTag,
  isReachable,
  MEMBER_ERRORS,
  METER_STATUS_LABELS,
  type MemberFormValues,
  PHONE_STATUS_LABELS,
  toMemberFields,
} from '@horof/shared';
import { Alert, App, Button, Result, Skeleton, Tag } from 'antd';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery } from 'urql';
import { MemberDocuments } from './MemberDocuments';
import { MemberForm } from './MemberForm';
import { MemberDetailsDocument, UpdateMemberDocument } from './members-data';
import { ShartPeople } from './ShartPeople';

/**
 * A member: to look up, to send documents, to correct on site (doc 67), and
 * to add a meter installed there. Shart people and the status are changed
 * on the desk.
 */
export function MemberPage() {
  const { memberId = '' } = useParams();
  const { message } = App.useApp();
  const [{ data, error }] = useQuery({
    query: MemberDetailsDocument,
    variables: { id: memberId },
  });
  const [{ fetching: saving }, updateMember] =
    useMutation(UpdateMemberDocument);
  const [editing, setEditing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (!data) {
    return (
      <div className="page">
        <BackBar to="/members" label="الأعضاء" />
        {error ? (
          <Alert type="error" showIcon message="تعذّر تحميل العضو" />
        ) : (
          <Skeleton active paragraph={{ rows: 8 }} />
        )}
      </div>
    );
  }
  const member = data.member;
  if (!member) {
    return (
      <div className="page">
        <BackBar to="/members" label="الأعضاء" />
        <Result status="404" title="العضو غير موجود" />
      </div>
    );
  }
  const left = member.status === 'LEFT';

  async function save(values: MemberFormValues) {
    if (!member) return;
    // Send only what changed.
    const patch = Object.fromEntries(
      Object.entries(toMemberFields(values)).filter(
        ([key, value]) =>
          value !== ((member as Record<string, unknown>)[key] ?? null),
      ),
    );
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setProblem(null);
    const result = await updateMember({ id: member.id, patch });
    if (result.error) {
      setProblem(
        describeError(result.error, 'تعذّر حفظ التعديلات.', MEMBER_ERRORS),
      );
      return;
    }
    setEditing(false);
    message.success('حُفظت التعديلات');
  }

  if (editing) {
    return (
      <div className="page">
        <BackBar to="/members" label="الأعضاء" />
        <h1 className="member-title">تعديل: {member.fullName}</h1>
        {problem && <Alert type="error" showIcon message={problem} />}
        <MemberForm
          initialValues={{
            firstName: member.firstName,
            fatherName: member.fatherName,
            lastName: member.lastName,
            sex: member.sex,
            memberAddress: member.memberAddress,
            phone: member.phone ?? '',
            contactPhone: member.contactPhone ?? '',
            contactName: member.contactName ?? '',
            notifyChannel: member.notifyChannel,
            email: member.email ?? '',
            committeeRole: member.committeeRole,
          }}
          saving={saving}
          submitLabel="حفظ التعديلات"
          onSubmit={save}
          onCancel={() => {
            setEditing(false);
            setProblem(null);
          }}
        />
      </div>
    );
  }

  const meters = member.meters.nodes.flatMap((m) => (m ? [m] : []));

  return (
    <div className="page">
      <BackBar to="/members" label="الأعضاء" />
      <section className="card">
        <h1 className="member-title">{member.fullName}</h1>
        <div className="field-hint">{member.memberAddress}</div>
        <dl className="details">
          <dt>الهاتف</dt>
          <dd>
            {member.notifyPhone ? (
              <a href={`tel:${member.notifyPhone}`} className="ltr-num">
                {member.notifyPhone}
              </a>
            ) : (
              '—'
            )}
            {member.contactPhone && member.contactName && (
              <div className="field-hint">({member.contactName})</div>
            )}
          </dd>
          <dt>التواصل</dt>
          <dd>
            <Tag color={isReachable(member) ? 'green' : 'orange'}>
              {member.notifyChannel === 'NONE'
                ? CHANNEL_LABELS.NONE
                : `${CHANNEL_LABELS[member.notifyChannel]} · ${PHONE_STATUS_LABELS[member.phoneStatus ?? 'MISSING']}`}
            </Tag>
          </dd>
          {member.committeeRole && (
            <>
              <dt>اللجنة</dt>
              <dd>
                <Tag color="purple">
                  {COMMITTEE_ROLE_LABELS[member.committeeRole]}
                </Tag>
              </dd>
            </>
          )}
          <dt>منذ</dt>
          <dd>{fullDate(member.joinedAt)}</dd>
        </dl>
        {!isReachable(member) && member.notifyChannel !== 'NONE' && !left && (
          <Alert
            type="warning"
            showIcon
            message="لا تصل هذا العضوَ رسائل واتساب"
            description={
              member.phoneStatus === 'LANDLINE'
                ? 'رقمه ثابت. أضف رقم هاتف محمول (له أو لقريب).'
                : 'أضف رقم هاتف محمول صحيح (له أو لقريب).'
            }
          />
        )}
        {left ? (
          <div className="field-hint">غادر الجمعية: لا تُعدَّل معلوماته.</div>
        ) : (
          <Button
            type="primary"
            size="large"
            block
            onClick={() => setEditing(true)}
          >
            تعديل المعلومات
          </Button>
        )}
        <div className="field-hint">الشرط وحالة العضو تُعدَّل من الحاسوب.</div>
      </section>

      <MemberDocuments
        member={{ ...member, unpaidCount: member.unpaid.totalCount }}
        associationName={data.currentSettings?.associationName ?? 'الجمعية'}
      />
      <ShartPeople
        people={member.shartPeople.nodes.flatMap((p) => (p ? [p] : []))}
      />

      <section className="card">
        <div className="field-label">العدادات ({meters.length})</div>
        {meters.length === 0 && (
          <div className="field-hint">لا عداد لهذا العضو.</div>
        )}
        <ul className="plain-list">
          {meters.map((m) => (
            <li key={m.id}>
              <Link to={`/meters/${m.id}`} className="meter-line">
                <span>
                  <strong>{m.label}</strong>
                  {m.meterAddress !== m.label && (
                    <div className="field-hint">{m.meterAddress}</div>
                  )}
                </span>
                <Tag
                  color={m.meterStatus === 'CONNECTED' ? 'green' : 'default'}
                >
                  {METER_STATUS_LABELS[m.meterStatus]}
                </Tag>
              </Link>
            </li>
          ))}
        </ul>
        <Link to={`/meters/new?member=${member.id}`}>
          <Button size="large" block>
            إضافة عداد (مع صورته)
          </Button>
        </Link>
      </section>
      <IdTag id={member.id} label="معرّف العضو" />
    </div>
  );
}
